import { errorToast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import type { ConfirmButtonProps } from "@/v2-backoffice-client/components/button"
import { useLayerAuthoringDeleteMutation } from "@/v2-backoffice-client/layer/authoring/layer.authoring.mutation"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import {
  LAYER_QUERY_NAME,
  LAYER_VIEW_QUERY_NAME,
} from "@/v2-backoffice-client/layer/layer.constants"
import { AUTHORING_MIRROR_ENVIRONMENT } from "@/v2-backoffice-client/feature/feature.constants"

export type LayerAuthoringDeleteButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  featureCode: string
}

/**
 * Deletes the layer from the catalog. The mutation also disables the
 * runtime row server-side (one atomic decision), so the layer disappears
 * from customers immediately and from every runtime on the next catalog
 * publish.
 */
function LayerAuthoringDeleteButton(props: LayerAuthoringDeleteButtonProps) {
  const { featureCode, children, ...buttonProps } = props

  const [deleteRow, { loading, error }] = useLayerAuthoringDeleteMutation({
    // The mutation also disables the runtime row server-side, so the layer
    // tables, the open sheet, and the tab counts all change.
    refetchQueries: [
      LAYER_AUTHORING_STATUSES_QUERY_NAME,
      LAYER_COUNTS_QUERY_NAME,
      LAYER_QUERY_NAME,
      LAYER_VIEW_QUERY_NAME,
    ],
  })
  errorToast(error, { title: "Failed to delete layer" })

  return (
    <ConfirmButton
      variant="destructive"
      isStacked
      iconSrc="trash"
      content="Disable the layer now and remove it from the catalog — the next catalog publish deletes it from every runtime."
      confirmContent="Delete this layer? It is disabled immediately and removed everywhere on the next catalog publish."
      confirmVariant="destructive"
      isLoading={loading}
      onConfirm={() => {
        deleteRow({
          variables: {
            input: {
              FeatureCode: featureCode,
              Environment: AUTHORING_MIRROR_ENVIRONMENT,
            },
          },
        })
      }}
      {...buttonProps}
    >
      {children ?? "Delete"}
    </ConfirmButton>
  )
}

export default LayerAuthoringDeleteButton
